import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';

import type { Observable } from 'rxjs';
import { from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { I18nService } from '@recruitee/i18n';
import { IntegrationsApiService } from '@recruitee/integrations-http';
import { IntegrationsClientConfig, IntegrationsCredentials } from '@recruitee/integrations-types';
import { DialogService } from '@recruitee/user-interface';

import { UrlInTranslationsPipe } from '../../common';
import type { IntegrationListLegalDialogData } from '../../misc/integration-list-legal-dialog/integration-list-legal-dialog.component';
import { CAMPAIGN_ANALYTICS_CONFIGS, HELP_ARTICLE_URL } from './constants';
import type { CampaignAnalyticsConfig, CampaignAnalyticsConfigData } from './types';

@Injectable({ providedIn: 'root' })
export class JobCampaignsService {
  public HELP_ARTICLE_URL = HELP_ARTICLE_URL;

  constructor(
    private urlInTranslationsPipe: UrlInTranslationsPipe,
    private dialogService: DialogService,
    private injector: Injector,
    private i18n: I18nService,
    private http: HttpClient,
    private config: IntegrationsClientConfig,
    private integrationsCredentials: IntegrationsCredentials,
  ) {}

  public getCampaignAnalyticsConfigs(): Observable<CampaignAnalyticsConfig[]> {
    return of(CAMPAIGN_ANALYTICS_CONFIGS);
  }

  public getCampaignAnalyticsConfigsData(): CampaignAnalyticsConfig[] {
    return CAMPAIGN_ANALYTICS_CONFIGS;
  }

  public getCampaignAnalyticsConfigData(codename: string): Observable<CampaignAnalyticsConfigData> {
    const api = this.buildApi(codename);

    return api
      .get('integration')
      .pipe(
        map(result =>
          result.integration
            ? { shareAnalytics: result.integration.shareAnalytics }
            : { shareAnalytics: false },
        ),
      );
  }

  public setCampaignAnalyticsConfigData(
    codename: string,
    value: CampaignAnalyticsConfigData,
  ): Observable<CampaignAnalyticsConfigData> {
    const api = this.buildApi(codename);

    return api.patch('integration', value).pipe(
      catchError(() => api.post('integration', value)),
      map(result => ({ shareAnalytics: result.integration.shareAnalytics })),
    );
  }

  // eslint-disable-next-line max-lines-per-function
  public openCampaignAnalyticsDialog(partnerName: string): Observable<boolean> {
    const buttonText = this.i18n.translate('rti.job_campaigns.settings.legal_dialog.accept');

    const data: IntegrationListLegalDialogData = {
      partnerName: partnerName,
      helpArticleUrl: HELP_ARTICLE_URL,
      buttonText: buttonText,
      textOverClaims: this.i18n.translate('rti.job_campaigns.settings.legal_dialog.paragraph_1', {
        button: buttonText,
      }),
      textUnderClaims: this.urlInTranslationsPipe.transform(
        this.i18n.translate('rti.job_campaigns.settings.legal_dialog.paragraph_2'),
        { a: 'target=_blank href=' + HELP_ARTICLE_URL },
      ),
      claims: [
        this.i18n.translate('rti.job_campaigns.settings.legal_dialog.list.1', {
          partner_name: partnerName,
        }),
        this.i18n.translate('rti.job_campaigns.settings.legal_dialog.list.2', {
          partner_name: partnerName,
        }),
        this.i18n.translate('rti.job_campaigns.settings.legal_dialog.list.3', {
          partner_name: partnerName,
        }),
        this.i18n.translate('rti.job_campaigns.settings.legal_dialog.list.4', {
          partner_name: partnerName,
        }),
      ],
    };

    return from(
      import('../../misc/integration-list-legal-dialog/integration-list-legal-dialog.component'),
    ).pipe(
      switchMap(({ IntegrationListLegalDialogComponent, IntegrationListLegalDialogConfig }) =>
        this.dialogService
          .openCustom(
            IntegrationListLegalDialogComponent,
            { ...IntegrationListLegalDialogConfig, injector: this.injector },
            data,
          )
          .afterClosed.pipe(map(result => !!result?.accepted)),
      ),
    );
  }

  private buildApi(codename: string): IntegrationsApiService {
    const api = new IntegrationsApiService(this.http, this.config, this.integrationsCredentials);
    api.context = codename;
    return api;
  }
}
